import { useEffect, useState } from "react";

const TOUCH_MQ = "(hover: none) and (pointer: coarse)";
const LANDSCAPE_MQ = "(orientation: landscape)";

function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

function readShouldBlock(): boolean {
  return matches(TOUCH_MQ) && matches(LANDSCAPE_MQ);
}

function subscribeMediaQuery(query: string, onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  const mq = window.matchMedia(query);
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }
  mq.addListener(onChange);
  return () => mq.removeListener(onChange);
}

/**
 * True when a touch device (phone / tablet, including iPad) is in landscape.
 * Desktop never matches — `hover: none` + `pointer: coarse` exclude fine pointers.
 */
export function useUnsupportedLandscape(): boolean {
  const [shouldBlock, setShouldBlock] = useState(readShouldBlock);

  useEffect(() => {
    const update = () => setShouldBlock(readShouldBlock());
    update();
    const unsubTouch = subscribeMediaQuery(TOUCH_MQ, update);
    const unsubLandscape = subscribeMediaQuery(LANDSCAPE_MQ, update);
    return () => {
      unsubTouch();
      unsubLandscape();
    };
  }, []);

  return shouldBlock;
}
